import { useSyncExternalStore } from 'react';

/**
 * A tiny external store for what a screen and its bar in the global Header share
 * (filters, the Archived switch, which buttons are enabled). Same shape as
 * useAsoebiOrdersBar / useProcurementBar, written once: a screen writes
 * with `set` from an effect, the Header's bar reads with `use`. A patch that
 * changes nothing is dropped, so publishing from an effect can never loop.
 */
export interface BarStore<T extends object> {
  get: () => T;
  set: (patch: Partial<T>) => void;
  reset: () => void;
  use: () => T;
}

export function createBarStore<T extends object>(initial: T): BarStore<T> {
  let state = initial;
  const listeners = new Set<() => void>();
  const emit = () => listeners.forEach(l => l());
  const get = () => state;
  const subscribe = (l: () => void) => {
    listeners.add(l);
    return () => { listeners.delete(l); };
  };
  return {
    get,
    set: patch => {
      const changed = (Object.keys(patch) as Array<keyof T>).some(k => patch[k] !== state[k]);
      if (!changed) return;
      state = { ...state, ...patch };
      emit();
    },
    reset: () => { state = initial; emit(); },
    use: () => useSyncExternalStore(subscribe, get, get),
  };
}
